import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { Translation, fetchTranslations, setPageNumber, setPageSize } from '../store/slices/translationsSlice';
import { openTranslationDetails } from '../store/slices/uiSlice';

import { selectPagination, selectStatusFilter, selectSearchQuery, selectTranslations, selectFetchLoading } from '../store/selectors/translationsSelectors';
import { closeTranslationDetails } from '../store/slices/uiSlice';

enum GridColumns {
  English = 'originalString',
  Spanish = 'translatedString',
  UpdatedBy = 'lastUpdatedBy',
  Date = 'date',
}

const TranslationGrid: React.FC = () => {
  const dispatch = useAppDispatch();
  const { totalRows, pageSize, page: pageNumber, nextToken: continuationToken } = useAppSelector(selectPagination);
  const searchText = useAppSelector(selectSearchQuery);
  const status = useAppSelector(selectStatusFilter);
  const translations = useAppSelector(selectTranslations);
  const translationsLoading = useAppSelector(selectFetchLoading);

  const handleRowClick = (translation: Translation) => {
    const id = String((translation as Translation).translationRecordId ?? translation.translationRecordId);
    dispatch(openTranslationDetails(id));
  };

  useEffect(() => {
    // Fetch translations when page, pageSize, status, or searchText changes
    dispatch(
      fetchTranslations({
        status,
        pageNumber,
        pageSize,
        searchText,
        language: 'es',
        continuationToken,
      })
    );
  }, [status, pageSize, pageNumber, searchText, continuationToken, dispatch]);

  // Close details drawer when status filter changes (tab navigation)
  useEffect(() => {
    dispatch(closeTranslationDetails());
  }, [status, dispatch]);

  // Handle pagination changes
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setPageSize(model.pageSize));
    dispatch(setPageNumber(model.page + 1));
  };

  const paginationModel = useMemo(
    () => ({
      page: pageNumber - 1,
      pageSize: pageSize,
    }),
    [pageNumber, pageSize]
  );

  const columns: GridColDef[] = [
    {
      field: GridColumns.English,
      headerName: 'English string',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => <Box sx={styles.truncatedCellBox}>{params.value}</Box>,
    },
    {
      field: GridColumns.Spanish,
      headerName: 'Spanish translation',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => <Box sx={styles.truncatedCellBox}>{params.value}</Box>,
    },
    {
      field: GridColumns.UpdatedBy,
      headerName: 'Last Updated By',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={styles.cellBox}>
          {params.value || 'Not edited yet'}
        </Typography>
      ),
    },
    {
      field: GridColumns.Date,
      headerName: 'Date',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={styles.cellBox}>
          {Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            hour12: true,
          }).format(new Date(params.row.date))}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={styles.gridContainer}>
      <DataGrid
        getRowId={(row) => row.translationRecordId}
        rows={translations}
        columns={columns}
        loading={translationsLoading}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        paginationMode="server"
        rowCount={totalRows}
        onRowClick={(rowParams) => handleRowClick(rowParams.row as Translation)}
        disableColumnMenu
        disableColumnSorting
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        disableColumnResize
        sx={styles.dataGridSx}
        slots={{
          columnResizeIcon() {
            return null;
          },
        }}
      />
    </Box>
  );
};

const styles = {
  gridContainer: {
    flex: 1, // Take up all available space in the parent flex container
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent overflow
    minHeight: 0, // Allow flexbox to work properly
  },
  dataGridSx: {
    flex: 1, // Take up all available space
    height: '100%', // Ensure full height
    '& .MuiDataGrid-cell': {
      py: 1,
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: 'grey.50',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-main': {
      overflow: 'hidden', // Prevent content overflow
    },
    '& .MuiDataGrid-virtualScroller': {
      overflow: 'auto', // Allow scrolling within the grid
    },
    border: 'none',
    borderTop: '1px solid #e0e0e0',
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    outline: 'none',
  },
  cellBox: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    py: 1,
  },
  truncatedCellBox: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    py: 1,
  },
};

export default TranslationGrid;
